import { queryAddresses } from "@/apis/tms/address";
import { useDebounce } from "@/hooks/shared/debounce";
import classNames from "classnames";
import { useQuery } from "@tanstack/react-query";
import { Button, Select, Spin, Tag } from "antd";
import { useMemo, useState } from "react";
import { PlusOutlined } from "@ant-design/icons";

interface AddressSelectProps {
  className?: string;
  selectClassName?: string;
  disabled?: boolean;
  value?: string;
  onChange: (value: string) => void;
}

function useTMSAddresses(allOverLike?: string) {
  return useQuery({
    queryKey: ["tms-addresses", allOverLike],
    queryFn: () => queryAddresses({ allOverLike }),
    placeholderData: (previousData) => previousData,
  });
}

export default function AddressSelect(props: AddressSelectProps) {
  const { className, selectClassName, disabled, value, onChange } = props;
  const [allOverLike, setAllOverLike] = useState(value);
  const debouncedAllOverLike = useDebounce(allOverLike, 500);
  const { data: addresses, isFetching } = useTMSAddresses(debouncedAllOverLike);

  const options = useMemo(() => {
    return addresses?.items.map((address) => ({
      label: (
        <div>
          <Tag color="blue" className="mr-2">
            {address.type}
          </Tag>
          {address.name && <span>{address.name} - </span>}
          {address.code && address.code !== address.name && (
            <span>{address.code} - </span>
          )}
          <span>{address.street}</span>
          <span>
            {address.city}, {address.state} {address.zip}
          </span>
        </div>
      ),
      value: `${address.id}`,
    }));
  }, [addresses]);

  return (
    <div className={classNames(className, "flex flex-row items-center gap-2")}>
      <Select
        className={selectClassName}
        disabled={disabled}
        placeholder="Type to search address"
        value={value}
        showSearch
        filterOption={false}
        onSearch={(value) => setAllOverLike(value)}
        notFoundContent={
          isFetching ? <Spin size="small" /> : "No results found"
        }
        allowClear
        onChange={onChange}
        options={options}
      />
      <Button icon={<PlusOutlined />} onClick={() => {}}>
        New address
      </Button>
    </div>
  );
}
